/**
 * Rendering instants the way a clinic reads them.
 *
 * The API speaks UTC instants; a receptionist reads Africa/Cairo wall time (FR-307). Every
 * conversion in the operational screens goes through here, so the zone is stated once rather than
 * assumed forty times — and so the day the product leaves Egypt, there is one file to change rather
 * than a search for `toLocaleTimeString`.
 */

export const CLINIC_ZONE = 'Africa/Cairo';

const TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: CLINIC_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `17:30`, in the clinic's zone, in Latin digits whatever the interface language is. */
export function clinicTime(instant: string | null | undefined): string {
  if (!instant) {
    return '—';
  }
  return TIME.format(new Date(instant));
}

/** Today in the clinic's zone as `YYYY-MM-DD` — the shape every date parameter in the API takes. */
export function clinicToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_ZONE }).format(now);
}

/**
 * The clinic-local day an instant fell on, as `YYYY-MM-DD`.
 *
 * Needed wherever a timestamp has to be shown as a date. Slicing the ISO string would read it in
 * UTC, and Cairo is two or three hours ahead — so anything that happened after ten in the evening
 * would be shown as having happened the day before (FR-307, and the M5 defect that taught it).
 */
export function clinicDay(instant: string | null | undefined, now: Date = new Date()): string {
  return clinicToday(instant ? new Date(instant) : now);
}

/** Shifts a `YYYY-MM-DD` by whole days without going near a time zone. */
export function shiftDay(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  return shifted.toISOString().slice(0, 10);
}

/** How a date reads in the sidebar and page headings, in the reader's language. */
export function longDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1)));
}

export type DayPart = 'morning' | 'afternoon' | 'evening';

/**
 * Which greeting the clinic's wall clock calls for: morning from five until noon, afternoon until
 * five, evening otherwise. Read in the clinic's zone rather than the browser's, so a laptop left on
 * UTC does not wish Cairo good morning at one in the afternoon.
 */
export function clinicDayPart(now: Date = new Date()): DayPart {
  const hour = Math.floor(minutesIntoDay(now.toISOString()) / 60);
  if (hour >= 5 && hour < 12) {
    return 'morning';
  }
  return hour >= 12 && hour < 17 ? 'afternoon' : 'evening';
}

/** Minutes from the start of the clinic day, used to place a card on the board's time axis. */
export function minutesIntoDay(instant: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CLINIC_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instant));
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
  return hour * 60 + minute;
}
